import { paintIconAction } from './82-icon-actions.mjs';
/** Practice and final timed results remain held for an explicit next action.
 * Timed board celebrations are transient; the active-time owner advances them.
 * This controller owns presentation only, never score submission or domain state. */
export function createGameProgression(
  ctx,
  playfield,
  advance,
  { before = null, anchor = null, onChapterChange = () => {} } = {},
) {
  const panel = ctx.e('div', 'game-completion');
  const title = ctx.e('strong');
  const detail = ctx.e('span');
  const next = ctx.e('button', 'game-next');
  next.type = 'button';
  panel.setAttribute('role', 'status');
  panel.setAttribute('aria-live', 'polite');
  panel.setAttribute('aria-hidden', 'true');
  panel.hidden = true;
  panel.append(title, detail, next);
  if (before) playfield.insertBefore(panel, before);
  else playfield.append(panel);
  let current = null,
    chapter = 1,
    arrival = null;
  // Keep a held result inside its artwork and clear of the live tools below.
  // Only presentation geometry is measured; resize never advances a chapter.
  function place() {
    if (!anchor || panel.hidden || !panel.style?.setProperty) return;
    const art = anchor.getBoundingClientRect?.(),
      host = playfield.getBoundingClientRect?.();
    const height = panel.getBoundingClientRect?.().height;
    if (!art || !host || ![art.top, art.bottom, host.top, height].every(Number.isFinite)) return;
    panel.style.setProperty(
      '--game-result-top',
      `${Math.max(art.top - host.top, art.bottom - host.top - height - 8)}px`,
    );
  }
  const resize = anchor && typeof ResizeObserver === 'function' ? new ResizeObserver(place) : null;
  resize?.observe(anchor);
  resize?.observe(panel);
  function reset() {
    arrival?.cancel();
    arrival = null;
    current = null;
    panel.hidden = true;
    panel.setAttribute('aria-hidden', 'true');
  }
  function proceed() {
    if (!current || next.disabled) return;
    const finished = current,
      action = finished.action;
    if (advance(action) === false) return;
    const completedRound =
      !action &&
      finished.state.completed === true &&
      !['stalled', 'move-limit'].includes(finished.reason);
    if (completedRound) chapter++;
    reset();
    // advance may synchronously paint the new board. Publish the accepted ordinal
    // afterwards so scenery never lags by a chapter or changes on rejection.
    if (completedRound) onChapterChange();
  }
  next.addEventListener('click', proceed);
  return {
    get chapter() {
      return chapter;
    },
    /** Decorative three-stage scenery cycles across two completed chapters;
     * this ordinal never changes domain level, score, seed or difficulty. */
    world(stage = 1) {
      return ((chapter - 1) % 2) * 3 + Math.min(3, Math.max(1, stage));
    },
    show(
      state,
      {
        creation = false,
        puzzle = false,
        timed = false,
        timedBoard = false,
        advanceAction = null,
        advanceLabel = null,
        completionTitle = null,
        stage = 1,
        reason = null,
      } = {},
    ) {
      if (current?.state === state && current?.timed === timed) return;
      current = { state, action: advanceAction, reason, timed };
      next.hidden = timedBoard;
      title.textContent =
        (timedBoard
          ? reason === 'move-limit'
            ? 'Move limit reached'
            : state.outcome === 'stalled'
              ? 'Board ended'
              : 'Board clear!'
          : completionTitle) ||
        (advanceAction
          ? `Level ${stage} complete!`
          : ['stalled', 'move-limit'].includes(reason)
            ? 'Round finished!'
            : creation
              ? 'Creation complete!'
              : puzzle
                ? 'Puzzle complete!'
                : timed
                  ? 'Round finished!'
                  : 'Challenge complete!');
      detail.textContent = timedBoard
        ? `${state.score.toLocaleString()} points · ${reason === 'move-limit' ? 'Clock continues' : 'Next board incoming'}`
        : reason === 'move-limit'
          ? 'This round has ended. Start fresh whenever you like.'
          : creation
            ? 'Made by you. Ready for a fresh canvas.'
            : puzzle
              ? 'Beautifully solved. Ready for another puzzle.'
              : `${state.score.toLocaleString()} points · Challenge ${chapter}`;
      const nextLabel = advanceAction
        ? advanceLabel || 'Next level'
        : creation
          ? 'Next canvas'
          : puzzle
            ? 'Next puzzle'
            : 'Next challenge';
      paintIconAction(ctx, next, 'chevron-right', nextLabel, nextLabel);
      panel.hidden = false;
      panel.setAttribute('aria-hidden', 'false');
      panel.setAttribute('aria-label', `${title.textContent} ${detail.textContent}`);
      place();
      arrival?.cancel();
      if (
        !window.matchMedia('(prefers-reduced-motion: reduce)').matches &&
        typeof panel.animate === 'function'
      )
        arrival = panel.animate(
          [
            { opacity: 0, transform: 'translateY(12px) scale(.96)' },
            { opacity: 1, transform: 'translateY(0) scale(1)' },
          ],
          { duration: 360, easing: 'cubic-bezier(.2,.8,.2,1)' },
        );
    },
    tick(_now, active) {
      next.disabled = !active;
    },
    reset,
    dispose() {
      resize?.disconnect();
      reset();
      next.removeEventListener('click', proceed);
      panel.remove();
    },
  };
}
